/**
 * Cliente del módulo «Registro biométrico» (KYC). Envuelve las callables `portal_kyc*`, que
 * verifican el rol admin y leen la tabla y el bucket del MISMO ambiente del portal (dev ve
 * dev, producción ve producción). El navegador nunca recibe credenciales de AWS: las
 * imágenes llegan como URLs prefirmadas de corta vida.
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

export type DecisionKyc = 'APPROVED' | 'REJECTED' | 'MANUAL_REVIEW' | 'UNKNOWN';

export interface ValidacionResumen {
  validationId: string;
  decision: DecisionKyc;
  createdAt: string;
  cedula: string;
  nombre: string;
  documentType: string;
  country: string;
  platform: string;
  appVersion: string;
  livenessConfidence: number;
  faceMatchScore: number;
  overallConfidence: number;
  imagenesBorradas: boolean;
}

export interface ValidacionDetalle extends ValidacionResumen {
  sessionId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  livenessMode: string;
  reasonCodes: string[];
  errorMessage: string | null;
  updatedAt: string;
  imagesDeletedAt: string | null;
  imagesDeletedReason: string | null;
  documentoUrl: string | null;
  selfieUrl: string | null;
  imagenesVigenciaSegundos: number;
}

export interface FiltrosValidaciones {
  decision?: DecisionKyc | '';
  cedula?: string;
  desde?: string;
  hasta?: string;
}

export interface PaginaValidaciones {
  items: ValidacionResumen[];
  /** null cuando no hay más páginas. */
  cursor: string | null;
}

export interface AgregadoKyc {
  total: number;
  porDecision: Record<string, number>;
  porPlataforma: Record<string, number>;
  porPais: Record<string, number>;
  porDocumento: Record<string, number>;
  sumaLiveness: number;
  nLiveness: number;
  sumaFaceMatch: number;
  nFaceMatch: number;
}

export interface EstadisticasKyc {
  desde: string;
  hasta: string;
  total: AgregadoKyc;
  serie: { fecha: string; total: number; porDecision: Record<string, number>; porPlataforma: Record<string, number> }[];
  /** Días pasados del rango que el cálculo diario todavía no completó. */
  diasSinCalcular: number;
  actualizadoEn: string;
}

async function llamar<Req, Res>(nombre: string, datos: Req): Promise<Res> {
  const fn = httpsCallable<Req, Res>(functions, nombre);
  return (await fn(datos)).data;
}

export const obtenerEstadisticasKyc = (filtros: { desde?: string; hasta?: string }) =>
  llamar<typeof filtros, EstadisticasKyc>('portal_kycEstadisticas', filtros);

export const listarValidacionesKyc = (filtros: FiltrosValidaciones & { cursor?: string | null; tamano?: number }) =>
  llamar<typeof filtros, PaginaValidaciones>('portal_kycValidaciones', filtros);

export const obtenerValidacionKyc = (validationId: string) =>
  llamar<{ validationId: string }, ValidacionDetalle>('portal_kycValidacionDetalle', { validationId });
